/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import Head from "next/head";
import Link from "next/link";
import { isElectron } from "react-device-detect";

import { FastCardImage } from "src/components/Splash/CardImage";
import { LDFooter } from "src/components/Splash/Footer";
import { LDHeader, HeaderLink } from "src/components/Splash/Header";
import { LDPageContainer } from "src/components/Splash/Layout/PageContainer";
import { LDPostPill } from "src/components/Splash/Layout/PostPill";
import {
  LDBlockHeading,
  LDBlockParagraph,
  LDVLink,
  LDSectionBlockHeading,
} from "src/components/Splash/Layout/Typography";
import { LDBlock, LDSectionBlock, LDLinkBlock } from "src/components/Splash/LDBlock";
import { AppRoutes } from "src/lib/constants";

export default function Jobs() {
  return (
    <>
      <Head>
        <title>Vienna Hypertext - Jobs</title>
        <link rel="icon" href="/favicon.png" />
      </Head>
      <main>
        <LDPageContainer>
          <LDHeader
            text="JOB BOARD"
            leftItem={
              <Link href={isElectron ? "/meta/index.html" : AppRoutes.Meta} passHref>
                <HeaderLink style={{ textAlign: "left", marginLeft: "20px" }}>HOME</HeaderLink>
              </Link>
            }
          />
          <LDBlock>
            <LDBlockHeading>About</LDBlockHeading>
            <LDBlockParagraph>
              Vienna Hypertext is a software company building an app for working with ideas.
            </LDBlockParagraph>
            <LDBlockParagraph>
              We are interested in computing ideas from the &apos;60s and &apos;70s, the kind that saw the computer
              as a tool for the intellectual and artist. One such idea, very influential, is{" "}
              <LDVLink href="https://en.wikipedia.org/wiki/Project_Xanadu">Project Xanadu</LDVLink>, whose creator
              coined the term hypertext.
            </LDBlockParagraph>
            <LDBlockParagraph>
              At root, we are seeking to reclaim the computer as a tool for thought by reviving ideas that came
              before their time. We believe in{" "}
              <LDVLink href="https://vienna.earth/plate/vht/Computer%20Renaissance">Computer Renaissance</LDVLink>,
              and see ourselves at the intersection of technology and philosophy.
            </LDBlockParagraph>
          </LDBlock>
          <LDSectionBlock>
            <LDSectionBlockHeading>Open Positions</LDSectionBlockHeading>
          </LDSectionBlock>
          <Link href={`${AppRoutes.Jobs}/frontend`} passHref>
            <LDLinkBlock>
              <LDBlockHeading>
                Frontend Engineer <LDPostPill>UPDATED</LDPostPill>
              </LDBlockHeading>
              <FastCardImage
                src="https://prod-vht-image-bucket.s3.amazonaws.com/fitzcarraldo.jpg"
                width={"100%"}
              />
            </LDLinkBlock>
          </Link>
          <Link href={`${AppRoutes.Jobs}/full-stack`} passHref>
            <LDLinkBlock>
              <LDBlockHeading>
                Full Stack Engineer <LDPostPill>UPDATED</LDPostPill>
              </LDBlockHeading>
              <FastCardImage src="https://prod-vht-image-bucket.s3.amazonaws.com/guysinfield.png" width={"100%"} />
            </LDLinkBlock>
          </Link>
          <Link href={`${AppRoutes.Jobs}/design`} passHref>
            <LDLinkBlock>
              <LDBlockHeading>
                Designer <LDPostPill>UPDATED</LDPostPill>
              </LDBlockHeading>
              <FastCardImage src="https://prod-vht-image-bucket.s3.amazonaws.com/architecten.jpg" width={"100%"} />
            </LDLinkBlock>
          </Link>
          <Link href={`${AppRoutes.Jobs}/byoj`} passHref>
            <LDLinkBlock>
              <LDBlockHeading>Build Your Own Job</LDBlockHeading>
              <FastCardImage src="https://prod-vht-image-bucket.s3.amazonaws.com/hippies.jpg" width={"100%"} />
            </LDLinkBlock>
          </Link>
          <LDFooter />
        </LDPageContainer>
      </main>
    </>
  );
}
